import { useState, useRef, useEffect, useMemo } from 'react';
import * as React from 'react';
import { colors, shadows } from '../features/employees/styles/personalakteStyles';

export interface PillOption<T extends string> {
  value: T;
  label: string;
  color: string;
  bg?: string;
}

interface PillFilterDropdownProps<T extends string> {
  options: PillOption<T>[];
  selected: Set<T>;
  onChange: (next: Set<T>) => void;
  label: string;
  clearLabel?: string;
}

function toggleSetValue<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

export default function PillFilterDropdown<T extends string>({
  options,
  selected,
  onChange,
  label,
  clearLabel = 'Alle anzeigen',
}: PillFilterDropdownProps<T>): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = selected.size > 0;

  const displayLabel = useMemo(() => {
    if (selected.size === 0) return label;
    if (selected.size === 1) {
      const match = options.find(o => selected.has(o.value));
      return match?.label ?? label;
    }
    return `${selected.size} ${label.replace(/^Alle\s+/, '')}`;
  }, [selected, options, label]);

  return (
    <div ref={ref} style={{ position: 'relative', zIndex: 1000 }}>
      <button
        onClick={() => setOpen(prev => !prev)}
        style={{
          padding: '6px 14px',
          borderRadius: '9999px',
          border: isActive ? `1px solid ${colors.magenta}50` : `1px solid ${colors.border}`,
          fontSize: '11px',
          fontWeight: 600,
          background: 'rgba(255, 255, 255, 0.9)',
          color: isActive ? colors.magenta : colors.textBody,
          cursor: 'pointer',
          outline: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          whiteSpace: 'nowrap' as const,
        }}
      >
        {displayLabel} <span style={{ fontSize: '8px' }}>&#9660;</span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '4px',
            background: colors.bgCard,
            borderRadius: '16px',
            padding: '16px',
            minWidth: '280px',
            boxShadow: shadows.elevated,
            border: `1px solid ${colors.border}`,
            zIndex: 1000,
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {options.map(pill => {
              const isOn = selected.has(pill.value);
              const hasBg = !!pill.bg;
              return (
                <div
                  key={pill.value}
                  role="button"
                  tabIndex={0}
                  onClick={() => onChange(toggleSetValue(selected, pill.value))}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onChange(toggleSetValue(selected, pill.value));
                    }
                  }}
                  style={{
                    padding: '7px 16px',
                    borderRadius: '9999px',
                    border: isOn ? `2px solid ${pill.color}` : '2px solid #e2e8f0',
                    background: isOn ? (hasBg ? pill.bg : pill.color) : 'transparent',
                    color: isOn ? (hasBg ? pill.color : 'white') : '#94a3b8',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {pill.label}
                </div>
              );
            })}
          </div>
          {isActive && (
            <button
              onClick={() => onChange(new Set())}
              style={{
                marginTop: '12px',
                padding: '4px 8px',
                borderRadius: '9999px',
                border: 'none',
                background: 'transparent',
                color: colors.textMuted,
                fontSize: '10px',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'center',
              }}
            >
              {clearLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
